import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Grid,
  Autocomplete,
  TextField,
} from "@mui/material";

import PageContainer from "src/components/container/PageContainer";
import DashboardCard from "src/components/shared/DashboardCard";
import { useSnackBar } from "src/context/SnackBarContext";
import DataTable from "src/components/DataTable";
import Loader from "src/components/Loader";
import { postData } from "../utilities/api";
import useFetchData from "../utilities/useFetchData";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

const paymentModes = ["Cash", "UPI", "Bank", "Card"];

const columns = [
  { field: "BillNo", headerName: "Bill No", width: 100 },
  { field: "Party", headerName: "Party", flex: 1 },
  { field: "Total", headerName: "Total", flex: 1 },
  { field: "Received", headerName: "Received", flex: 1 },
  { field: "PaymentMode", headerName: "Payment Mode", flex: 1 },
];

function SalesReport() {
  const { showSnackbar } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [selectedMode, setSelectedMode] = useState("");
  const [selectedType, setSelectedType] = useState("Counter");


  const { data, isLoading, error } = useFetchData(
    ["masterData"],
    ["/api/getMasterDatas"]
  );
  const partyData = data?.masterData?.data || [];
  const navigate = useNavigate();
  // console.log(partyData, isLoading, error);
  
  const navigateToPrint = (type) => {
    navigate("/sales_report_print", {
      state: {
        header: {
          fromDate: dayjs(fromDate).format("DD/MM/YYYY"),
          toDate: dayjs(toDate).format("DD/MM/YYYY"),
          party: selectedParty,
          branch: selectedBranch,

        },
        table: reportData,
        type: type,
      },
    });
  };
  const handleDateChange = (from, to) => {
    setFromDate(from);
    setToDate(to);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      branch: selectedBranch,
      party: selectedParty,
      paymentMode: selectedMode,
      fromDate: fromDate || dayjs(),
      toDate: toDate || dayjs(),
      type:selectedType
    };

    const result = await postData("/api/getSalesReport", payload);

    if (!result.success) {
      showSnackbar("error", result.message);
      setReportData([]);
    } else if (result.data.length === 0) {
      showSnackbar("warning", "No data found");
      setReportData([]);
    }else {
    
      const dataWithSi = result.data.map((row, index) => {
        let updatedReceiptType = row.PaymentMode;
    
        // Check for cash and received 0, set to "Pending"
        if ( row.Received === 0) {
          updatedReceiptType = "Pending";
          console.log("Pending", row.Received, row.PaymentMode);
          
        }
       
        return {
          si: index + 1,
          ...row,
          PaymentMode: updatedReceiptType,
        };
      });
    
      setReportData(dataWithSi);
    }

    setLoading(false);
  };

  return (
    <>
      <Loader start={loading || isLoading} />
      <PageContainer>
        <DashboardCard>
          <Typography variant="h4" mb={2}>
            Bill Report
          </Typography>

          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="From Date"
                    format="DD/MM/YYYY"
                    value={fromDate}
                    onChange={(date) => handleDateChange(date, toDate)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="To Date"
                    format="DD/MM/YYYY"
                    minDate={fromDate}
                    value={toDate}
                    onChange={(date) => handleDateChange(fromDate, date)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={["ALL", ...(partyData?.branch_list || [])]}
                    value={selectedBranch || "ALL"}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return `${option.label || ""}`.trim();
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (
                        typeof option === "string" ||
                        typeof value === "string"
                      ) {
                        return option === value;
                      }
                      return (
                        option.label === value.label &&
                        option.name === value.name
                      );
                    }}
                    onChange={(e, val) =>
                      setSelectedBranch(val === "ALL" ? null : val)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Branch" />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            {/* Remaining Grid for Party, Payment Mode, Submit Button */}
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={["ALL", ...(partyData?.customer_list || [])]}
                  value={selectedParty || "ALL"}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return `${option.label}-${option.name || ""}`.trim();
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (
                      typeof option === "string" ||
                      typeof value === "string"
                    ) {
                      return option === value;
                    }
                    return (
                      option.label === value.label && option.name === value.name
                    );
                  }}
                  onChange={(e, val) =>
                    setSelectedParty(val === "ALL" ? null : val)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Party" />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={["ALL", ...paymentModes]}
                  getOptionLabel={(option) => option}
                  value={selectedMode || "ALL"}
                  onChange={(e, val) =>
                    setSelectedMode(val === "ALL" ? "" : val)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment Mode"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>
               <Grid item xs={12} md={4}>
                <Autocomplete
                  options={["Counter","Quotation"]}
                  getOptionLabel={(option) => option}
                  value={selectedType}
                  onChange={(e, val) =>
                    setSelectedType(val)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Type"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{ mt: { xs: 1.5, md: 0 } }}
                  fullWidth
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Box>

          <DataTable columns={columns} rows={reportData} />
          {reportData.length > 0 && (
            <Box mt={3} p={2} border="1px solid #ccc" borderRadius={2}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total Sales: ₹
                    {reportData
                      .reduce((sum, row) => sum + Number(row.Total || 0), 0)
                      .toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total Credit: ₹
                    {reportData
                      .reduce((sum, row) => {
                        const total = Number(row.Total || 0);
                        const received = Number(row.Received || 0);
                        const credit = total - received;
                        // console.log(
                        //   `Row ${row.BillNo}: Total=${total}, Received=${received}, Credit=${credit}`
                        // );
                        return sum + credit;
                      }, 0)
                      .toFixed(2)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total Bank: ₹
                    {reportData
                 
                      .reduce((sum, row) => sum + Number(row.BankRecieved || 0), 0)
                      .toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <Button
                onClick={() => navigateToPrint(`${selectedType} Report`)}
                variant="contained"
                color="primary"
                fullWidth
              >
                Print
              </Button>
            </Grid>
          </Grid>
        </DashboardCard>
      </PageContainer>
    </>
  );
}

export default SalesReport;
